import { useEffect, useMemo, useRef, useState } from "react"
import { FloatingLabel, Form, InputGroup, ListGroup } from "react-bootstrap"

const AutoComplete = ({
	className,
	ops,
	sortKey,
	displayKey,
	displayKeys,
	onValueChanged,
	selectedKey,
	onSelectedClick,
	value,
	actionButton,
	actionButton2,
	placeH,
	title,
	filter,
	filterField,
	loadFunc,
	loadCondition,
	isBold,
	onBlurEvent,
	formatationFunc,
	onEscKeyDown,
	onEnterKeyDown,
	margT,
	hideComponent,
	disableComponent = false,
	disableSelect = false,
	margB,
	autoFocusConfig,
	onLoad,
	onMouseLv,
	// Performance helpers
	cacheKey,
	minChars = 0,
	maxItems,
	showListOnFocus = true,
	lazyLoad = false,
}) => {
	const [liItem, setListItem] = useState([])
	const [options, setOptions] = useState([])
	const [input, setInput] = useState("")
	const [hide, setHide] = useState(true)
	const [onLoaded, setOnLoaded] = useState(false)
	const cacheAppliedRef = useRef(false)

	// simple in-memory cache between mounts
	const cacheStore = useMemo(() => {
		if (!window.__AUTO_COMPLETE_CACHE__) {
			window.__AUTO_COMPLETE_CACHE__ = new Map()
		}
		return window.__AUTO_COMPLETE_CACHE__
	}, [])

	const sortOptions = (options, key) => {
		if (!key || !Array.isArray(options)) return options
		// Create a copy before sorting to avoid mutating read-only arrays (e.g. from Redux)
		return [...options].sort((a, b) =>
			String(a[key]).localeCompare(String(b[key]))
		)
	}

	useEffect(() => {
		if (value) setInput(value)
		else setInput("")
	}, [value])

	useEffect(() => {
		const sortedOptions = sortOptions(ops, sortKey)
		setListItem(sortedOptions)
		setOptions(sortedOptions)
	}, [ops, sortKey])

	useEffect(() => {
		const loadFunction = async () => {
			if (!(loadCondition && loadFunc)) return

			// Se estiver em modo lazy e sem cache, evita carregar até atingir minChars
			if (lazyLoad && minChars > 0 && showListOnFocus === false && (!value || String(value).length < minChars)) {
				return
			}

			// Try cached
			const key = cacheKey ? `${cacheKey}${filter ? `:${filterField}:${filter}` : ''}` : null
			if (key && cacheStore.has(key)) {
				const cached = cacheStore.get(key)
				setListItem(cached)
				setOptions(cached)
				cacheAppliedRef.current = true
				return callOnLoadFunction(cached)
			}

			try {
				const res = await loadFunc()
				let newOps = res?.content ? res.content : res
				newOps = Array.isArray(newOps) ? newOps.filter(c => c != null) : []
				if (filter) {
					newOps = newOps.filter((op) => op[filterField] === filter)
				}
				const sortedOptions = sortOptions(newOps, sortKey)
				setListItem(sortedOptions)
				setOptions(sortedOptions)
				if (key) cacheStore.set(key, sortedOptions)
				callOnLoadFunction(newOps)
			} catch (error) {
				setListItem([])
			}
		}
		loadFunction()
	}, [loadCondition, filter, filterField, sortKey, onLoad, cacheKey, lazyLoad, minChars, showListOnFocus, value])

	function callOnLoadFunction (data) {
		if (onLoad && !onLoaded) {
					setOnLoaded(true)
					onLoad(data)
				}
	}
	const onFieldUpdate = (value) => {
		let newListItem
		const search = value ? String(value).toLowerCase() : ""
		const canSearch = search.length >= minChars
		if (canSearch) {
			// Lazy fetch on demand if no options loaded
			if (lazyLoad && options.length === 0 && loadCondition && loadFunc) {
				loadFunc()
					.then((res) => {
						let newOps = res?.content ? res.content : res
						newOps = Array.isArray(newOps) ? newOps.filter(c => c != null) : []
						const sortedOptions = sortOptions(newOps, sortKey)
						setOptions(sortedOptions)
						setListItem(sortedOptions)
					})
					.catch(() => {})
			}
			// Verificação de segurança: só filtra se options existe e tem elementos
			if (Array.isArray(options) && options.length > 0) {
				newListItem = options.filter((liI) => {
					let textToTest
					if (formatationFunc) {
						textToTest = formatationFunc(liI)
					} else if (displayKey) {
						textToTest = liI[displayKey]
					} else if (displayKeys) {
						textToTest = keysJoinner(liI)
					} else {
						textToTest = liI
					}
					return String(textToTest).toLowerCase().includes(search)
				})
			} else {
				// Se não há options, mantém lista vazia mas permite digitação
				newListItem = []
			}
			setListItem(newListItem)
		} else {
			setListItem(Array.isArray(options) ? options : [])
		}
		onValueChanged && onValueChanged(value)
		setInput(value)
		setHide(!canSearch && (!Array.isArray(options) || options.length === 0))
	}

	const clear = () => {
		setInput("")
	}

	const onOpSelected = (li, index, lItem) => {
		if (formatationFunc) {
			setInput(formatationFunc(li))
		} else if (displayKey) {
			setInput(li[displayKey])
		} else if (displayKeys) {
			setInput(keysJoinner(li))
		} else {
			setInput(li)
		}

		onSelectedClick(li, index, lItem)
		setHide(true)
	}

	const keysJoinner = (li) => {
		if (!displayKeys || !Array.isArray(displayKeys)) return ""
		const textToRender = displayKeys.map((key) => `${li[key]} `)
		return textToRender.join("").trim()
	}

	const renderMoreThanOneKey = (li, index) => {
		const buildedString = keysJoinner(li)
		return <li key={index}>{buildedString}</li>
	}

	const boldedPart = (text, key) => {
		return <li key={key}>{text}</li>
	}

	const displayListItens = (li, index) => {
		if (formatationFunc) {
			return boldedPart(formatationFunc(li), index)
		}
		if (displayKey) {
			return boldedPart(li[displayKey], index)
		}
		if (displayKeys) {
			return renderMoreThanOneKey(li, index)
		}
		return boldedPart(li, index)
	}

	const onKeyDownHandler = (event) => {
		if (event.key === "Escape") {
			setHide(true)
			onEscKeyDown && onEscKeyDown()
		}
	}

	return (
		<div
			className={`${className}`}
			style={{ marginTop: margT || 4, marginBottom: margB || 4, position: "relative" }}
			onBlur={(e) => {
				setTimeout(() => {
					if (onBlurEvent) onBlurEvent(e, input)
					setHide(true)
				}, 200)
			}}
			onKeyDown={(event) => onKeyDownHandler(event)}
			onMouseLeave={() => {
				setHide(true)
			}}
		>
			{!hideComponent && (
				<InputGroup>
					<FloatingLabel
						style={{ zIndex: 0 }}
						label={title}
						controlId="floatingInput"
					>
						<Form.Control
							autoFocus={autoFocusConfig}
							disabled={disableComponent || disableSelect}
							placeholder={placeH}
							autoComplete="off"
							value={input}
							onClickCapture={() => {
								const canOpen = showListOnFocus && (input?.length || 0) >= minChars
								setHide(!canOpen)
							}}
							onChange={(event) =>
								onFieldUpdate(event.currentTarget.value)
							}
							onKeyDown={(e) => {
								if (e.key === 'Enter' && onEnterKeyDown) {
									onEnterKeyDown(input)
								}
							}}
							type="text"
						></Form.Control>
					</FloatingLabel>
					{!disableComponent && actionButton && actionButton(clear)}
					{!disableComponent && actionButton2 && actionButton2(input)}
				</InputGroup>
			)}

			{liItem && (
				<ListGroup 
					className="listgroup-autocomplete shadow-sm" 
					hidden={hide}
					style={{
						position: 'absolute',
						top: '100%',
						left: 0,
						width: '100%',
						maxHeight: '250px',
						overflowY: 'auto',
						zIndex: 1050,
						backgroundColor: '#fff'
					}}
				>
					{(maxItems ? liItem.slice(0, maxItems) : liItem).map((li, index) => (
						<ListGroup.Item
							key={index}
							action
							onClick={() => onOpSelected(li, index, liItem)}
						>
							{displayListItens(li, index)}
						</ListGroup.Item>
					))}
				</ListGroup>
			)}
		</div>
	)
}

export default AutoComplete
